import React, { useEffect, useRef, useState } from "react";
import { Loader, Send } from "lucide-react";
import { toast } from "react-toastify";
import api from "~/services/api";
import { Attachments, AttachmentGrid, C, EVENT_ICON, Icon, StatusChip, buildFormData, errorMessage, formatDate, useFileDrop } from "~/components/SupportChat/tickets";

const ACTION_TEXT = {
  created: "opened the ticket",
  approved: "approved",
  rejected: "rejected the ticket",
  changes_requested: "requested changes",
  start: "started working on the ticket",
  assign: "took over the ticket",
  close: "closed the ticket",
  archive: "archived the ticket",
  version_created: "created a new version",
  escalate: "escalated the ticket to a higher level for a solution",
  return: "sent the ticket back to a previous level",
};

function Message({ event, mine }) {
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] rounded-2xl px-4 py-2 flex flex-col gap-1 ${mine ? "bg-primary text-white rounded-br-sm" : "bg-gray-100 text-gray-800 rounded-bl-sm"}`}>
        <div className="flex items-center gap-2 text-[11px]">
          <strong>{event.actor?.name}</strong>
          {event.internal && <span className="font-bold rounded-full px-2 border" style={{ color: C.amber, borderColor: C.amber, background: "#fff" }}>Internal</span>}
        </div>
        {event.note && <div className="text-sm whitespace-pre-wrap break-words">{event.note}</div>}
        <AttachmentGrid items={event.attachments} />
        <div className={`text-[10px] text-right ${mine ? "text-white/70" : "text-gray-500"}`}>{formatDate(event.created_at)}</div>
      </div>
    </div>
  );
}

function Step({ event, showNote }) {
  const [icon, color] = EVENT_ICON[event.action] || ["Circle", C.textMid];
  return (
    <div className="flex flex-col items-center gap-1 py-1">
      <div className="flex flex-wrap items-center justify-center gap-2 text-xs text-gray-500">
        <Icon name={icon} size={13} color={color} />
        <strong className="text-gray-700">{event.actor?.name}</strong>
        <span>{ACTION_TEXT[event.action] || event.action}</span>
        {event.to_status && event.to_status !== event.from_status && <StatusChip status={event.to_status} />}
        {showNote && event.internal && <span className="font-bold rounded-full px-2 border text-[10px]" style={{ color: C.amber, borderColor: C.amber }}>Internal</span>}
        <span className="text-[10px]">{formatDate(event.created_at)}</span>
      </div>
      {showNote && event.note && <div className="max-w-[80%] text-xs text-gray-600 whitespace-pre-wrap break-words text-center">{event.note}</div>}
      {!!event.attachments?.length && <AttachmentGrid items={event.attachments} />}
    </div>
  );
}

function Composer({ ticketId, internal, onDone, onSent }) {
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const drop = useFileDrop(setFiles);

  async function submit() {
    if (busy || (!note.trim() && !files.length)) return;
    setBusy(true);
    try {
      await api.post(`/tickets/${ticketId}/comments`, buildFormData({ note: note.trim(), internal: internal ? "true" : undefined }, files));
      setNote("");
      setFiles([]);
      await onDone();
      onSent();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div {...drop.handlers} className="border-t pt-3 pb-16 pr-16 flex flex-col gap-2" style={{ outline: drop.over ? "2px dashed #ee5827" : "none" }}>
      <div className="flex items-end gap-2">
        <Attachments files={files} setFiles={setFiles} />
        <textarea
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={internal ? "Internal note. The person who opened the ticket will not see it." : "Type a message to the person who opened the ticket."}
          className="flex-1 border rounded-2xl px-4 py-2 text-sm outline-none resize-none"
        />
        <button type="button" disabled={busy} onClick={submit} className="flex items-center gap-2 text-xs font-bold bg-primary text-white rounded-md px-4 h-10 shrink-0 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50">
          {busy ? <Loader size={14} className="animate-spin" /> : <Send size={14} />} Send
        </button>
      </div>
    </div>
  );
}

const STEPS = ["escalate", "return"];

export default function Timeline({ ticket, onDone }) {
  const list = useRef(null);
  const staff = ticket.staff_level > 0;
  const [channel, setChannel] = useState("conversation");
  const internal = staff && channel === "internal";
  const events = ticket.events.filter((event) => (internal ? event.internal : !event.internal || STEPS.includes(event.action)));
  const scrollToEnd = () => {
    requestAnimationFrame(() => {
      if (list.current) list.current.scrollTop = list.current.scrollHeight;
    });
  };

  useEffect(() => {
    scrollToEnd();
  }, [events.length, channel]);

  return (
    <div className="w-full px-4 pb-6 flex flex-col gap-3" style={{ height: "62vh", minHeight: 360 }}>
      {staff && (
        <div className="flex gap-2">
          {[["conversation", "Conversation"], ["internal", "Internal"]].map(([key, label]) => (
            <button key={key} type="button" onClick={() => setChannel(key)} className={`text-xs font-bold rounded-full px-3 py-1 border cursor-pointer ${channel === key ? "bg-primary text-white border-primary" : "bg-white text-primary border-primary"}`}>
              {label}
            </button>
          ))}
        </div>
      )}
      <div ref={list} className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3 pr-1">
        {events.map((event) =>
          event.action === "comment" ? (
            <Message key={event.id} event={event} mine={event.actor_id === ticket.viewer_id} />
          ) : (
            <Step key={event.id} event={event} showNote={internal || !STEPS.includes(event.action)} />
          )
        )}
      </div>
      {ticket.actions.comment && <Composer key={channel} ticketId={ticket.id} internal={internal} onDone={onDone} onSent={scrollToEnd} />}
    </div>
  );
}
